import {
  pushSubscribeResponseSchema,
  pushSubscriptionJsonSchema,
  pushUnsubscribeResponseSchema,
  type PushSubscribeResponse,
  type PushSubscriptionJson,
  type PushUnsubscribeResponse,
} from '@agrogruda/shared/schemas';
import { apiRequest } from '@/lib/api';
import { currentRegistration, registerAppServiceWorker, swSupported } from './serviceWorker';

/** VAPID public key (VITE_VAPID_PUBLIC_KEY); empty → subscribing is unavailable. */
export function vapidPublicKey(): string {
  const value: unknown = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  return typeof value === 'string' ? value.trim() : '';
}

/** Standard Web Push helper: URL-safe base64 VAPID key → applicationServerKey bytes. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported(): boolean {
  return (
    swSupported() && typeof window !== 'undefined' && 'Notification' in window && 'PushManager' in window
  );
}

export type PermissionState = NotificationPermission | 'unsupported';

export function notificationPermission(): PermissionState {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission;
}

export const pushApi = {
  subscribe: (subscription: PushSubscriptionJson, userAgent: string): Promise<PushSubscribeResponse> =>
    apiRequest('/api/push/subscribe', pushSubscribeResponseSchema, {
      method: 'POST',
      body: { subscription, userAgent },
    }),
  unsubscribe: (endpoint: string): Promise<PushUnsubscribeResponse> =>
    apiRequest('/api/push/subscribe', pushUnsubscribeResponseSchema, {
      method: 'DELETE',
      body: { endpoint },
    }),
};

/** `PushSubscription.toJSON()` validated against the API request schema (keys must be present). */
export function subscriptionJson(subscription: PushSubscription): PushSubscriptionJson {
  return pushSubscriptionJsonSchema.parse(subscription.toJSON());
}

/** Reuses the browser's existing subscription or creates one with the VAPID key. */
export async function ensureBrowserSubscription(
  registration: ServiceWorkerRegistration,
  applicationServerKey: string,
): Promise<PushSubscription> {
  const existing = await registration.pushManager.getSubscription();
  if (existing) return existing;
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(applicationServerKey),
  });
}

export type EnablePushResult = 'subscribed' | 'denied' | 'default' | 'unsupported' | 'unavailable' | 'failed';

export interface EnablePushOptions {
  /** Ask for permission when it is still 'default' (needs a user gesture in most browsers). */
  request: boolean;
  userAgent?: string;
}

/**
 * Full client flow (task.md §4.8): service worker → permission → PushManager.subscribe(VAPID) →
 * POST /api/push/subscribe { subscription, userAgent }. Every outcome is a result, never an exception.
 */
export async function enablePush(options: EnablePushOptions): Promise<EnablePushResult> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const key = vapidPublicKey();
  if (key === '') return 'unavailable';
  let registration: ServiceWorkerRegistration | null;
  try {
    registration = await registerAppServiceWorker();
  } catch {
    return 'unavailable';
  }
  if (!registration) return 'unavailable';
  if (Notification.permission === 'default') {
    if (!options.request) return 'default';
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'default';
  }
  try {
    const subscription = await ensureBrowserSubscription(registration, key);
    await pushApi.subscribe(subscriptionJson(subscription), options.userAgent ?? navigator.userAgent);
    return 'subscribed';
  } catch {
    return 'failed';
  }
}

/**
 * Logout: DELETE /api/push/subscribe { endpoint } (while the session token is still valid) and drop the browser
 * subscription. Returns true when a subscription was removed.
 */
export async function disablePush(): Promise<boolean> {
  if (!pushSupported()) return false;
  let registration: ServiceWorkerRegistration | null;
  try {
    registration = await currentRegistration();
  } catch {
    return false;
  }
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return false;
  try {
    await pushApi.unsubscribe(subscription.endpoint);
  } catch {
    // the row is also cleaned by the API on 404/410 and by the worker's push-cleanup job
  }
  try {
    await subscription.unsubscribe();
  } catch {
    return false;
  }
  return true;
}
